import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Box } from 'foundations-components/transitional/components/Box';
import { Flex } from 'foundations-components/components/Flex';
import { H6 } from 'foundations-components/transitional/components/H6';
import { Text } from 'foundations-components/components/Text';
import styled from 'foundations-theming/utils';
import { rgba } from 'foundations-components/transitional/utils/color';
import { ProgressBarWithTooltip } from 'academy-common-lib/components/card/ProgressBarWithTooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useClipRangeCalculations = (clipRange, videoLength) => {
  return useMemo(() => {
    const clampedStartTime = Math.max(0, Math.min(clipRange.startTime, videoLength));
    const clampedEndTime = Math.max(clampedStartTime, Math.min(clipRange.endTime, videoLength));
    const calculatedStartPercent = clampedStartTime / videoLength * 100;
    const calculatedEndPercent = clampedEndTime / videoLength * 100;
    const calculatedWidthPercent = calculatedEndPercent - calculatedStartPercent;
    const calculatedClipLength = clampedEndTime - clampedStartTime;
    return {
      clipLength: calculatedClipLength,
      startPercent: calculatedStartPercent,
      widthPercent: calculatedWidthPercent
    };
  }, [clipRange, videoLength]);
};
const formatVideoLength = seconds => {
  const totalSeconds = Math.max(0, Math.round(seconds));
  if (totalSeconds <= 59) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};
const NoPointer = styled.div.withConfig({
  displayName: "ClipVideoOverlay__NoPointer"
})(["pointer-events:none;"]);
const TopGradient = styled.div.withConfig({
  displayName: "ClipVideoOverlay__TopGradient"
})(["position:absolute;inset-block-start:0;inline-size:100%;background:linear-gradient( 180deg,", ",transparent );"], ({
  theme
}) => rgba(theme.color['fill-accent-neutral-default'], 0.5));
const BottomGradient = styled.div.withConfig({
  displayName: "ClipVideoOverlay__BottomGradient"
})(["position:absolute;inset-block-end:0;inset-inline-start:0;inline-size:100%;background:linear-gradient( 0deg,", ",transparent );"], ({
  theme
}) => rgba(theme.color['fill-accent-neutral-default'], 0.5));
export const ClipVideoOverlay = ({
  clipRange,
  videoLength
}) => {
  const {
    clipLength,
    startPercent,
    widthPercent
  } = useClipRangeCalculations(clipRange, videoLength);
  return /*#__PURE__*/_jsxs(NoPointer, {
    children: [/*#__PURE__*/_jsx(TopGradient, {
      children: /*#__PURE__*/_jsx(Box, {
        padding: "300",
        children: /*#__PURE__*/_jsx(H6, {
          color: "text-core-on-fill-default",
          textTransform: "none",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "video.hubSpotAcademyVideo"
          })
        })
      })
    }), /*#__PURE__*/_jsx(BottomGradient, {
      children: /*#__PURE__*/_jsx(Box, {
        paddingBlock: "200",
        paddingInline: "300",
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "200",
            inlineSize: "100%",
            children: [/*#__PURE__*/_jsx(Box, {
              grow: 1,
              position: "relative",
              children: /*#__PURE__*/_jsx(ProgressBarWithTooltip, {
                clipLength: clipLength,
                clipStartTime: clipRange.startTime,
                startPercent: startPercent,
                widthPercent: widthPercent
              })
            }), /*#__PURE__*/_jsx(Text, {
              color: "text-core-on-fill-default",
              children: formatVideoLength(videoLength)
            })]
          })
        })
      })
    })]
  });
};
export default ClipVideoOverlay;